import { get, post, put } from '@/lib/api/client'
import {
  PaginationParamsSchema,
  StrategicPillarFilterParamsSchema,
  type StrategicPillar,
  type StrategicPillarDetail,
  type StrategicPillarFilterParams,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class StrategicPillarService {
  /**
   * Filter strategic pillars with pagination (Public endpoint)
   * POST /strategic-pillar/public/filter?page={page}&size={size}
   */
  async getStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = StrategicPillarFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<StrategicPillar>>>(
      `strategic-pillar/public/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Search strategic pillars (Public endpoint)
   * GET /strategic-pillar/public/search?searchBy={searchTerm}&size={size}&page={page}
   */
  async searchStrategicPillars(
    searchTerm: string,
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const query = new URLSearchParams({
      searchBy: searchTerm,
      size: validatedParams.size.toString(),
      page: validatedParams.page.toString()
    }).toString()

    return await get<ApiResponse<PaginatedResponse<StrategicPillar>>>(
      `strategic-pillar/public/search?${query}`
    )
  }

  /**
   * Get strategic pillar by ID (Public endpoint)
   * GET /strategic-pillar/public/{strategicPillarId}
   */
  async getStrategicPillarById(strategicPillarId: string): Promise<ApiResponse<StrategicPillarDetail>> {
    return await get<ApiResponse<StrategicPillarDetail>>(
      `strategic-pillar/public/${strategicPillarId}`
    )
  }

  /**
   * Get landing page strategic pillars (priorities)
   * POST /strategic-pillar/public/filter with landingPagePrioritiesOfAfsa filter
   */
  async getLandingPageStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    return this.getStrategicPillars(params, {
      ...filters,
      landingPagePrioritiesOfAfsa: true
    })
  }

  /**
   * Get "Who We Are" strategic pillars
   * POST /strategic-pillar/public/filter with whoWeAre filter
   */
  async getWhoWeAreStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    return this.getStrategicPillars(params, {
      ...filters,
      whoWeAre: true
    })
  }

  /**
   * Get countries strategic pillars
   * POST /strategic-pillar/public/filter with countries filter
   */
  async getCountriesStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    return this.getStrategicPillars(params, {
      ...filters,
      countries: true
    })
  }

  /**
   * Get history strategic pillars
   * POST /strategic-pillar/public/filter with history filter
   */
  async getHistoryStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    return this.getStrategicPillars(params, {
      ...filters,
      history: true
    })
  }

  /**
   * Get member strategic pillars
   * POST /strategic-pillar/public/filter with member filter
   */
  async getMemberStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    return this.getStrategicPillars(params, {
      ...filters,
      member: true
    })
  }

  /**
   * Get all published strategic pillars
   * POST /strategic-pillar/public/filter
   */
  async getPublishedStrategicPillars(
    params: PaginationParams = { page: 1, size: 10 },
    filters: StrategicPillarFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<StrategicPillar>>> {
    return this.getStrategicPillars(params, filters)
  }

  /**
   * Increment view count for a strategic pillar
   * PUT /strategic-pillar/public/{strategicPillarId}/increment-view
   */
  async incrementView(strategicPillarId: string): Promise<ApiResponse<void>> {
    return await put<ApiResponse<void>>(
      `strategic-pillar/public/${strategicPillarId}/increment-view`
    )
  }

  /**
   * Get all strategic pillars (public endpoint)
   * GET /strategic-pillar/public/all
   */
  async getAllStrategicPillars(): Promise<ApiResponse<StrategicPillar[]>> {
    return await get<ApiResponse<StrategicPillar[]>>(
      'strategic-pillar/public/all'
    )
  }

  /**
   * Get strategic pillar highlights
   * GET /strategic-pillar/public/highlights?type={type}
   */
  async getStrategicPillarHighlights(type: string = 'HOME_PAGE_PRIORITIES_OF_AFSA'): Promise<ApiResponse<StrategicPillar[]>> {
    return await get<ApiResponse<StrategicPillar[]>>(
      `strategic-pillar/public/highlights?type=${type}`
    )
  }
}

// Export singleton instance
export const strategicPillarService = new StrategicPillarService()

// Export default
export default strategicPillarService
